"use client";

import type { ChatStructuredDimensionScore } from "../types";
import React from "react";
import { cn } from "utils";

export interface DimensionScoreListProps {
  items: ChatStructuredDimensionScore[];
  /** 首行是否使用深色底（Alpha Picks 情况一） */
  highlightFirst?: boolean;
  /** 情况二：主分颜色为深绿 rank 样式 */
  rankStyle?: "brand" | "rankGreen";
  className?: string;
}

const scoreClass = (rankStyle: DimensionScoreListProps["rankStyle"]) =>
  rankStyle === "rankGreen"
    ? "title_l text-content_accent1_primary"
    : "button_l text-content_brand_primary";

/**
 * 维度得分列表：首行可选高亮卡片，其余带分隔线。
 */
export const DimensionScoreList: React.FC<DimensionScoreListProps> = ({
  items,
  highlightFirst = false,
  rankStyle = "brand",
  className,
}) => {
  if (!items.length) {
    return null;
  }
  const [first, ...rest] = items;
  const Row = ({
    item,
    boxed,
    showBorder,
  }: {
    item: ChatStructuredDimensionScore;
    boxed?: boolean;
    showBorder?: boolean;
  }) => (
    <div
      className={cn(
        "flex items-center justify-between py-3",
        showBorder && "border-b border-border_white_transparent5",
        boxed && "rounded-2 bg-background_white_overlay5 px-2 py-3",
      )}
    >
      <span
        className={cn(
          "body_m",
          rankStyle === "rankGreen"
            ? "text-content_secondary"
            : "text-content_tertiary",
        )}
      >
        {item.label}
      </span>
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className={scoreClass(rankStyle)}>{item.score}</span>
        <span className="body_medium_s text-content_quaternary">
          {item.rankLabel}
        </span>
      </div>
    </div>
  );

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      <div className="flex flex-col">
        {highlightFirst ? (
          <Row item={first} boxed />
        ) : (
          <Row item={first} showBorder={rest.length > 0} />
        )}
        <div className={cn(highlightFirst && "px-2")}>
          {rest.map((item, idx) => (
            <Row
              key={`${item.label}-${idx}`}
              item={item}
              showBorder={idx < rest.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
